import { ReactElement, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/_atoms/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/_atoms/tooltip";
import IconCompose from "@/components/_icons/IconCompose";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { ModuleActionGuard } from "@/domain/_shared/module-access/components/module-action-guard";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { useCreateMunicipalPropertyRegister } from "@/domain/municipal-property/hooks/api/use-create-municipal-property-register";
import { isActiveRegisterConflict } from "@/domain/municipal-property/lib/is-active-register-conflict";
import { CurrentWorkspace } from "@/domain/workspace/models/current-workspace-model";

import { MunicipalPropertyRegisterCreateForm } from "./municipal-property-register-create-form";

interface MunicipalPropertyRegisterCreateDialogProps {
  workspace: CurrentWorkspace;
  defaultYear?: number;
  fixedYear?: number;
}

export function MunicipalPropertyRegisterCreateDialog({
  workspace,
  defaultYear = new Date().getFullYear(),
  fixedYear,
}: MunicipalPropertyRegisterCreateDialogProps): ReactElement | null {
  const { t } = useDomainTranslation("municipal-property");
  const [isOpen, setIsOpen] = useState(false);
  const createRegister: ReturnType<typeof useCreateMunicipalPropertyRegister> = useCreateMunicipalPropertyRegister();
  const { hasPermission: canManageWorkspace } = useCheckPermission({
    resource: "workspace",
    action: "manage",
    resourceId: workspace.id,
  });

  async function handleCreate(): Promise<void> {
    if (fixedYear === undefined) {
      setIsOpen(true);
      return;
    }
    try {
      await createRegister.mutateAsync({ workspaceId: workspace.id, year: fixedYear });
      toast.success(t("register.create.success"));
    } catch (error) {
      toast.error(isActiveRegisterConflict(error) ? t("register.create.conflict") : t("register.create.error"));
    }
  }

  if (!canManageWorkspace) {
    return null;
  }

  return (
    <>
      <ModuleActionGuard module="MUNICIPAL_PROPERTY" action="create">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="xs"
                aria-label={t("register.createButton")}
                onClick={(): void => void handleCreate()}
                disabled={createRegister.isPending}
                data-testid="create-municipal-property-register-button"
              >
                <IconCompose className="mr-2" />
                {t("register.createButton")}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("register.createButton")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </ModuleActionGuard>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("register.create.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("register.create.dialogDescription")}</DialogDescription>
          </DialogHeader>
          <MunicipalPropertyRegisterCreateForm
            workspaceId={workspace.id}
            defaultYear={defaultYear}
            onSuccess={() => setIsOpen(false)}
            onCancel={() => setIsOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
